import { localTimeZone } from './timezone'
/**
 * Date formatting for the one date that carries a time: `dueAt`.
 *
 * The target end date has always been a full instant, and the create dialog now asks for the
 * hour rather than pinning it to 23:59:59. A date shown on its own is therefore misleading —
 * "due Mar 4" reads as "any time on the 4th" when the real deadline may be 09:00. Every
 * surface that shows a due date goes through here so the three of them cannot drift.
 */

/** "4 Mar, 17:00" — the board tile and table rows, where space is tight. */
export function formatDueShort(iso: string): string {
  const date = new Date(iso)
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', timeZone: localTimeZone() })}, ${formatTime(date)}`
}

/** "4 Mar 2026, 17:00" — the drawer, where the year matters. */
export function formatDueLong(iso: string | null): string {
  if (!iso) return '—'
  const date = new Date(iso)
  return `${date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: localTimeZone() })}, ${formatTime(date)}`
}

/** A date with no time component — start and completion dates, which have no hour to show. */
export function formatDay(iso: string | null): string {
  return iso
    ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: localTimeZone() })
    : '—'
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', timeZone: localTimeZone() })
}

const MINUTES_PER_HOUR = 60
/** Wall-clock days. The backend measures elapsed time, not working hours, so 1d = 24h. */
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR

/**
 * A duration in minutes, as "2d 4h" / "3h 20m" / "45m".
 *
 * Two units at most, largest first: a lead time of "4d 2h 17m" pretends to a precision the
 * measurement does not have, and nobody reads the third unit anyway. The smaller unit is
 * dropped entirely when it is zero ("2d", not "2d 0h").
 *
 * `null` is NOT zero. Every insights distribution has its own denominator and returns null
 * when nothing supplied the data — rendering that as "0m" would claim work was picked up
 * instantly. It renders as an em dash, exactly like the other formatters here.
 */
export function formatMinutes(minutes: number | null | undefined): string {
  if (minutes == null || !Number.isFinite(minutes)) return '—'

  const total = Math.max(0, Math.round(minutes))
  if (total < MINUTES_PER_HOUR) return `${total}m`

  if (total < MINUTES_PER_DAY) {
    const hours = Math.floor(total / MINUTES_PER_HOUR)
    const rest = total % MINUTES_PER_HOUR
    return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`
  }

  const days = Math.floor(total / MINUTES_PER_DAY)
  const hours = Math.floor((total % MINUTES_PER_DAY) / MINUTES_PER_HOUR)
  return hours === 0 ? `${days}d` : `${days}d ${hours}h`
}

/** `YYYY-MM-DD` for a `<input type="date">`, in LOCAL time rather than UTC. */
export function toDateInput(date: Date): string {
  const local = new Date(date)
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset())
  return local.toISOString().slice(0, 10)
}

/**
 * `YYYY-MM-DDTHH:mm` for a `<input type="datetime-local">`, in LOCAL time.
 *
 * Same offset correction as `toDateInput`, and for the same reason: `toISOString()` is UTC, so
 * seeding the field from it moves a 09:00 meeting by however many hours the reader is from
 * Greenwich. Null in, empty string out — an empty datetime input is how "not scheduled" is
 * spelled.
 */
export function toDateTimeInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const local = new Date(date)
  local.setMinutes(local.getMinutes() - local.getTimezoneOffset())
  return local.toISOString().slice(0, 16)
}

/** A locale-aware exact timestamp for tooltips and audit-oriented screens. */
export function formatExactDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short',
    timeZone: localTimeZone(),
  })
}

/**
 * Compact relative time for scan-heavy surfaces. The exact timestamp belongs in a tooltip,
 * so "2h ago" never throws away the information needed for an audit or SLA decision.
 */
export function formatRelativeTime(
  iso: string | null | undefined,
  nowMs = Date.now(),
): string {
  if (!iso) return '—'
  const date = new Date(iso)
  const value = date.getTime()
  if (Number.isNaN(value)) return '—'

  const deltaSeconds = Math.round((value - nowMs) / 1_000)
  const abs = Math.abs(deltaSeconds)
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' })
  if (abs < 60) return rtf.format(deltaSeconds, 'second')
  const minutes = Math.round(deltaSeconds / 60)
  if (Math.abs(minutes) < 60) return rtf.format(minutes, 'minute')
  const hours = Math.round(minutes / 60)
  if (Math.abs(hours) < 24) return rtf.format(hours, 'hour')
  const days = Math.round(hours / 24)
  if (Math.abs(days) < 30) return rtf.format(days, 'day')
  const months = Math.round(days / 30)
  if (Math.abs(months) < 12) return rtf.format(months, 'month')
  const years = Math.round(days / 365)
  return rtf.format(years, 'year')
}
